"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Switch } from "@/app/_module/components/ui/switch";
import { Badge } from "@/app/_module/components/ui/badge";
import { Phone, AlertTriangle, Loader2, ShieldAlert } from "lucide-react";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import api from "@/app/_module/services/api";
import { useToast } from "@/app/_module/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";

export default function AdminCallingToggle() {
  const { user } = useUserContext();
  const { toast } = useToast();
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingValue, setPendingValue] = useState(false);

  const agencyId = AgencyTokenManager.getCurrentAgencyId() || user?.currentAgency?.agency?.id;
  const role = user?.currentAgency?.role?.toLowerCase?.() || "";
  const isOwner = ["owner", "agency_owner"].includes(role);

  const fetchSettings = useCallback(async () => {
    if (!agencyId) return;
    try {
      const res = await api.get({
        url: getApiUrl(endpoint.agency.getAdminCallingSettings(agencyId)),
      });
      const data = (res as any)?.data?.data ?? (res as any)?.data ?? res;
      setEnabled(data?.allowAdminCalling === true);
    } catch {
      // Default to false
    } finally {
      setLoading(false);
    }
  }, [agencyId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleToggleRequest = (value: boolean) => {
    if (value) {
      // Enabling requires confirmation
      setPendingValue(true);
      setShowConfirm(true);
    } else {
      // Disabling is immediate
      saveToggle(false);
    }
  };

  const saveToggle = async (value: boolean) => {
    if (!agencyId || saving) return;
    setSaving(true);
    try {
      await api.put({
        url: getApiUrl(endpoint.agency.updateAdminCallingSettings(agencyId)),
        body: { allowAdminCalling: value },
      });
      setEnabled(value);
      toast({
        title: value ? "Admin Calling Enabled" : "Admin Calling Disabled",
        description: value
          ? "Agency managers can now make calls and send SMS."
          : "Agency managers are restricted from calling and SMS.",
      });
    } catch {
      toast({ title: "Failed to update", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // Only agency owners can see this — not admins (they shouldn't toggle their own permissions)
  if (!isOwner) return null;

  return (
    <>
      <Card className="border border-gray-200 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">Allow Agency Manager Calling Permissions</h3>
                  {enabled && (
                    <Badge variant="secondary" className="bg-amber-50 text-amber-700 text-[10px] font-medium">
                      Enabled
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-md">
                  By default, agency managers cannot make calls or send SMS.
                  When enabled, they can call and text agency owner leads too.
                </p>
                {enabled && (
                  <div className="flex items-center gap-1.5 mt-2">
                    <AlertTriangle className="w-3 h-3 text-amber-500" />
                    <span className="text-[11px] text-amber-600 font-medium">
                      Agency managers can now call and text leads using their own phone numbers and minutes.
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="shrink-0 pt-1">
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
              ) : (
                <Switch
                  checked={enabled}
                  onCheckedChange={handleToggleRequest}
                  disabled={saving}
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 text-amber-600" />
              </div>
              <AlertDialogTitle>Enable Admin Calling?</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-3">
              <span className="block">
                This will allow agency managers (admins) to make calls and send SMS using their own phone numbers and minutes.
              </span>
              <span className="block text-amber-700 font-medium">
                Admins will be responsible for their own usage charges.
              </span>
              <span className="block text-gray-500 text-xs">
                You can disable this at any time to revoke admin calling permissions.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => saveToggle(pendingValue)}
            >
              Accept & Enable
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
